import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PreviewDrawer } from "@ippon/shared-components";
import ManagementTaskDetailPage from "@/pages/ManagementTasks/Detail";
import ManagementTaskResultDetailPage from "@/pages/ManagementTaskResults/Detail";
import type { ManagementTaskRecord } from "@/store/managementTaskSlice";

export type ManagementPreviewMode = "task" | "result";

interface Props {
  open: boolean;
  onClose: () => void;
  /** "task"=管理業務詳細 / "result"=月別実績。null の間は中身を描画しない */
  mode: ManagementPreviewMode | null;
  taskId: string | null;
  /** result モード時のみ使用 */
  year: number;
  /** result モード時のみ使用 */
  month: number;
  propertyId: string;
  /** パンくず表示用 物件名 (全画面遷移時に location.state へ付与) */
  propertyName?: string;
  onTaskUpdated?: (taskId: string) => void;
  onResultUpdated?: () => void;
}

/**
 * 点検/清掃の単一プレビュードロワー。
 *
 * 管理業務詳細(task)と月別実績(result)を 1 つの PreviewDrawer で出し分ける。
 * 物件構成のドロワーと同様、タスク⇔各月を行き来してもドロワーは開いたまま
 * 中身(子ページ)だけが差し替わる (mode/対象が変わると key で再マウント)。
 */
const ManagementPreviewDrawer: React.FC<Props> = ({
  open,
  onClose,
  mode,
  taskId,
  year,
  month,
  propertyId,
  propertyName,
  onTaskUpdated,
  onResultUpdated,
}) => {
  const navigate = useNavigate();
  const [loadedTaskName, setLoadedTaskName] = useState<string | null>(null);

  // モード/対象が変わったらタイトルキャッシュをリセット
  useEffect(() => {
    setLoadedTaskName(null);
  }, [mode, taskId, year, month]);

  const handleLoaded = (rec: ManagementTaskRecord) => {
    setLoadedTaskName(rec.name || null);
  };

  // 全画面ボタン: 現在のモードに応じたフルページへ遷移
  const openPage = () => {
    if (!taskId || !propertyId) return;
    const qs = new URLSearchParams();
    qs.set("property_id", propertyId);
    if (mode === "result") {
      qs.set("year", String(year));
      qs.set("month", String(month));
      navigate(`/management-task-results/${taskId}?${qs.toString()}`, {
        state: {
          propName: propertyName,
          propertyId,
          title: `${year}年${String(month).padStart(2, "0")}月`,
          taskName: loadedTaskName,
          fromSource: "management-task-results",
        },
      });
    } else {
      navigate(`/management-tasks/${taskId}?${qs.toString()}`, {
        state: {
          propName: propertyName,
          propertyId,
          title: loadedTaskName,
          fromSource: "management-tasks",
        },
      });
    }
    onClose();
  };

  return (
    <PreviewDrawer open={open} onClose={onClose} onExpand={openPage}>
      {mode === "task" && taskId && propertyId && (
        <ManagementTaskDetailPage
          key={`task:${taskId}`}
          overrideTaskId={taskId}
          overridePropertyId={propertyId}
          embedded
          onUpdated={onTaskUpdated}
          onLoaded={handleLoaded}
        />
      )}
      {mode === "result" &&
        taskId &&
        propertyId &&
        Number.isFinite(year) &&
        Number.isFinite(month) && (
          <ManagementTaskResultDetailPage
            key={`result:${taskId}:${year}:${month}`}
            overrideTaskId={taskId}
            overridePropertyId={propertyId}
            overrideYear={year}
            overrideMonth={month}
            embedded
            onUpdated={onResultUpdated}
            onLoaded={handleLoaded}
          />
        )}
    </PreviewDrawer>
  );
};

export default ManagementPreviewDrawer;
